import { GrowthBook, type FeatureApiResponse } from '@growthbook/growthbook'
import { Browser } from '@gifsa/user-id/lib/data/navigator'
import { parse } from '@gifsa/user-id/lib/data/id'
import { App } from '@capacitor/app'
import type { Features } from '../config/features'

function isBot(id: string | undefined): boolean {
  if (id) {
    const parsedId = parse(id)
    if (parsedId && 'browser' in parsedId) {
      const { browser } = parsedId
      return (
        browser === Browser.googlebot || browser === Browser.bingbot || browser === Browser.yandex
      )
    }
  }
  return false
}

export default defineNuxtPlugin<{
  features: {
    getValue: <K extends keyof Features>(flagKey: K) => Features[K] | null
    getAllFeatures: <K extends keyof Features>() => Array<{ key: K; value: Features[K] | null }>
    updateFeatureValue: <K extends keyof Features>(flagKey: K, value: Features[K]) => void
  } | null
}>({
  name: 'features',
  dependsOn: ['language', 'cookie'],
  parallel: true,
  async setup() {
    const logger = useLogger('plugin:features')
    try {
      const { $lang, $cookie, payload } = useNuxtApp()
      const {
        name,
        growthBook: { clientKey },
      } = useRuntimeConfig().public

      if (name === '@gifsa/call-abroad') {
        return { provide: { features: null } }
      }

      const features = useState<FeatureApiResponse | undefined>('features')

      if (!clientKey && !import.meta.prerender && !features.value) {
        logger.warn('GrowthBook is disabled')
        return { provide: { features: null } }
      }

      const growthbook = new GrowthBook<Features>({
        apiHost: 'https://cdn.growthbook.io',
        clientKey,
        enableDevMode: process.env.NODE_ENV === 'development',
      })

      const route = useRoute()
      const forcedFlags = import.meta.prerender
        ? {
            features: {
              simsurf_experiment: { defaultValue: route.query.simsurf_experiment || 'off' },
            },
          }
        : features.value
      if (import.meta.prerender && route.query.simsurf_experiment && payload.path) {
        payload.path = payload.path.replace(
          `?simsurf_experiment=${String(route.query.simsurf_experiment)}`,
          '',
        )
      }

      const initResponse = await growthbook.init({ payload: forcedFlags })
      if (initResponse.error) {
        throw initResponse.error
      }
      if (import.meta.server) {
        features.value = growthbook.getPayload()
      }

      const theWindow = typeof window !== 'undefined' ? window : undefined
      const event = useRequestEvent()
      const { version: appVersion } = useRuntimeConfig().public
      const attributes = {
        id: $cookie.value.getId() || 'anonymous',
        lang: $lang,
        app: __IS_MOBILE_APP__ ? 'mobile' : 'website',
        path: route.path,
        device: $cookie.value.getDevice() || 'desktop',
        geoLoc: $cookie.value.getGeoLoc() || '',
        userAgent: theWindow?.navigator.userAgent ?? event?.node.req.headers['user-agent'] ?? '',
        abGroup: isBot($cookie.value.getId()) ? '' : $cookie.value.getAbGroup() || '',
        isBot: isBot($cookie.value.getId()),
        buildNumber:
          __IS_MOBILE_APP__ && process.env.NODE_ENV !== 'development'
            ? (await App.getInfo()).build
            : undefined,
        appVersion,
      }
      await growthbook.setAttributes(attributes)
      logger.debug({ attributes }, 'GrowthBook Attributes')

      function getValue<K extends keyof Features>(flagKey: K): Features[K] | null {
        return growthbook.evalFeature(flagKey).value
      }

      function getAllFeatures<K extends keyof Features>() {
        return Object.keys(growthbook.getFeatures()).map((key: string) => ({
          key: key as K,
          value: getValue(key as K),
        }))
      }

      function updateFeatureValue<K extends keyof Features>(flagKey: K, value: Features[K]) {
        const feature = new Map<K, Features[K]>([[flagKey, value]])
        growthbook.setForcedFeatures(feature)
      }

      return { provide: { features: { getValue, getAllFeatures, updateFeatureValue } } }
    } catch (error) {
      logger.error(error, 'Failed to load feature flags from GrowthBook')
      return { provide: { features: null } }
    }
  },
})
